import { formatDollars } from '@fruitful/fruitils';
import type { Activity, ActivityDate } from '@fruitful/types';
import { Link, Redirect, router, useLocalSearchParams } from 'expo-router';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ScreenContainer,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { IS_PRODUCTION } from '../../../config/FruitfulEnvironment';
import { useGetExpandedApexAccountDetails } from '../../hooks/useGetApexAccountDetails';

const formatActivityDate = (date: ActivityDate): string => {
    return `${date.month}/${date.day}/${date.year}`;
};

export const ApexAccountDev = () => {
    const { apexAccountId } = useLocalSearchParams<{
        apexAccountId: string;
    }>();
    const { isLoading, data: apexAccountDetails } =
        useGetExpandedApexAccountDetails(apexAccountId);

    if (IS_PRODUCTION) {
        return <Redirect href="/home/investment" />;
    }

    return (
        <ScreenContainer
            title="Investment Account Dev Details"
            subtitle={`${apexAccountDetails?.apexAccountId ?? ''} / ${apexAccountDetails?.accountNumber ?? ''}`}
            hasBackButton
        >
            {isLoading ? (
                <YStack
                    alignItems="center"
                    justifyContent="center"
                    height={200}
                >
                    <Spinner />
                </YStack>
            ) : (
                <>
                    <YStack
                        paddingHorizontal={32}
                        gap={16}
                        alignItems="flex-start"
                    >
                        {apexAccountDetails?.positionsDetails.map((p) => {
                            return (
                                <XStack
                                    gap={32}
                                    key={p.assetId}
                                    flex={1}
                                    width="100%"
                                >
                                    <BodyMediumRegular width="25%">
                                        {p.symbol}
                                    </BodyMediumRegular>
                                    <BodyMediumRegular>
                                        {p.description}
                                    </BodyMediumRegular>
                                </XStack>
                            );
                        }) ?? <BodyMediumRegular>No entries</BodyMediumRegular>}
                    </YStack>
                    {/* Activities Table */}
                    {(apexAccountDetails?.activities?.length ?? 0) > 0 && (
                        <YStack
                            paddingHorizontal={32}
                            paddingTop={24}
                            gap={12}
                            width="100%"
                        >
                            <BodyLargeEmphasized>
                                Activities
                            </BodyLargeEmphasized>
                            <ActivitiesTable
                                activities={
                                    apexAccountDetails?.activities ?? []
                                }
                            />
                        </YStack>
                    )}
                </>
            )}

            {apexAccountId != null && (
                <YStack
                    paddingHorizontal={32}
                    paddingTop={24}
                    paddingBottom={16}
                    width="100%"
                    gap={12}
                >
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        text="Simulate ICT Deposit"
                        onPress={() => {
                            router.navigate({
                                pathname: '/investments/simulate-ict-deposit',
                                params: { apexAccountId },
                            });
                        }}
                    />
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        text="Simulate ICT Withdrawal"
                        onPress={() => {
                            router.navigate({
                                pathname:
                                    '/investments/simulate-ict-withdrawal',
                                params: { apexAccountId },
                            });
                        }}
                    />
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        text="Simulate ACAT Transfer"
                        onPress={() => {
                            router.navigate({
                                pathname: '/investments/simulate-acat',
                                params: { apexAccountId },
                            });
                        }}
                    />
                    <Link
                        href={`https://sites.uat.apexascend.com/accounts/${apexAccountId}/summary`}
                        target="_blank"
                        style={{
                            alignSelf: 'center',
                        }}
                    >
                        <BodyMediumRegular textDecorationLine="underline">
                            Apex Ascend Workbench
                        </BodyMediumRegular>
                    </Link>
                </YStack>
            )}
        </ScreenContainer>
    );
};

const ActivitiesTable = ({ activities }: { activities: Activity[] }) => {
    if (activities.length === 0) {
        return <BodyMediumRegular>No activities</BodyMediumRegular>;
    }

    return (
        <YStack gap={8} width="100%">
            {/* Header Row */}
            <XStack
                gap={8}
                paddingVertical={8}
                borderBottomWidth={1}
                borderBottomColor="$gray400"
            >
                <BodySmallRegular width="15%" fontWeight="bold">
                    Date
                </BodySmallRegular>
                <BodySmallRegular width="35%" fontWeight="bold">
                    Description
                </BodySmallRegular>
                <BodySmallRegular width="15%" fontWeight="bold">
                    Amount
                </BodySmallRegular>
                <BodySmallRegular width="15%" fontWeight="bold">
                    Category
                </BodySmallRegular>
                <BodySmallRegular width="15%" fontWeight="bold">
                    State
                </BodySmallRegular>
            </XStack>
            {/* Data Rows */}
            {activities.map((activity) => (
                <XStack
                    key={activity.activityId}
                    gap={8}
                    paddingVertical={6}
                    borderBottomWidth={1}
                    borderBottomColor="$gray200"
                >
                    <BodySmallRegular width="15%">
                        {formatActivityDate(activity.activityDate)}
                    </BodySmallRegular>
                    <BodySmallRegular width="35%" numberOfLines={1}>
                        {activity.activityDescription}
                    </BodySmallRegular>
                    <BodySmallRegular width="15%">
                        {formatDollars(parseFloat(activity.grossAmount.value))}
                    </BodySmallRegular>
                    <BodySmallRegular width="15%">
                        {activity.subtypeCategory}
                    </BodySmallRegular>
                    <BodySmallRegular width="15%">
                        {activity.state}
                    </BodySmallRegular>
                </XStack>
            ))}
        </YStack>
    );
};
